import * as React from "react";
import { KeyValue } from "../interfaces";
import { Box, Grid, Theme, Typography } from "@mui/material";
import AddressDisplay from "./AddressDisplay";
import {
  addressLookup,
  formatDate,
  toTitleCase,
} from "../common/services/Utilities";
import {
  BRANCH_ADDRESS,
  DEFAULT_ADDRESS,
  OFFICE_ADDRESS,
  PHYSICAL_ADDRESS,
} from "../refdata/constants";
import LocationMap from "./LocationMap";
import makeStyles from "@mui/styles/makeStyles";

interface Props {
  data: KeyValue;
  officeFields?: boolean;
  useDefaultAddress?: boolean;
  showDateAcquired?: boolean;
  useBranchAddress?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  eventDetails: {
    "& .MuiGrid-item": {
      "paddingBottom": 8,
      "& p, div": {
        lineHeight: 1.3,
        color: "#666666",
      },
      "& strong": {
        color: theme.palette.primary.main,
      },
    },
  },
  mapUnavailable: {
    height: 200,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    flexDirection: "column",
    textAlign: "center",
    background: theme.palette.secondary.main,
    fontSize: "1.25rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
}));

export default function OfficeMapDisplay({
  data,
  officeFields,
  useDefaultAddress,
  showDateAcquired,
  useBranchAddress,
}: Props) {
  const classes = useStyles();
  const EventDetail = (key: string, label: string, value: any) =>
    value ? (
      <Grid
        id={`event-detail-${key}-${label}`}
        container
        spacing={2}
        className={classes.eventDetails}
        key={`${key}-container`}
      >
        <Grid item xs={12} key={`${key}-grid-item`}>
          <Typography id={`${key}-title`} align="left" key={`${key}-title`}>
            <strong>{label}</strong>
          </Typography>
          <Typography id={`${key}-value`} key={`${key}-value`} component="div">
            {value}
          </Typography>
        </Grid>
      </Grid>
    ) : (
      ""
    );

  const rightColumn: KeyValue = {
    "Branch Address": (
      <AddressDisplay
        addressObject={
          addressLookup(data)[
            useBranchAddress
              ? BRANCH_ADDRESS
              : useDefaultAddress
              ? DEFAULT_ADDRESS
              : officeFields
              ? OFFICE_ADDRESS
              : PHYSICAL_ADDRESS
          ]
        }
        wrapAddress={true}
        align={"left"}
      />
    ),
    "UNINUM": data.UNINUM || data.UNINUMBR,
    "Service Type": officeFields
      ? toTitleCase(data.OFF_SERVTYPE_DESC)
      : toTitleCase(data.SERVTYPE_DESC) || data.BRSERTYP,
    "Date Established":
      formatDate(data.ESTDATE || data.ESTYMD) || data.SIMS_ESTABLISHED_DATE,
    "Date Acquired": showDateAcquired ? formatDate(data.ACQDATE) : "",
  };

  const latitudeValue =
    officeFields && (data.LATITUDE || data.OFF_LATITUDE)
      ? data.OFF_LATITUDE
      : data.LATITUDE || data.SIMS_LATITUDE;
  const longitudeValue =
    officeFields && (data.LONGITUDE || data.OFF_LONGITUDE)
      ? data.OFF_LONGITUDE
      : data.LONGITUDE || data.SIMS_LONGITUDE;

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        {latitudeValue && longitudeValue ? (
          <Box>
            <LocationMap
              lat={latitudeValue}
              lng={longitudeValue}
              zoom={14}
              height={200}
              width="100%"
            />
          </Box>
        ) : (
          <Box className={classes.mapUnavailable}>Map Unavailable</Box>
        )}
      </Grid>
      <Grid item xs={5}>
        {Object.keys(rightColumn).map((key: string) => {
          return EventDetail(
            key.replace(/\s+/g, "-").toLowerCase(),
            key,
            rightColumn[key],
          );
        })}
      </Grid>
    </Grid>
  );
}
